// ============================================================================
// API SERVICE - Support Material (VERSIÓN CORREGIDA CON LOGS)
// ============================================================================

import {
  SupportMaterialFile,
  FolderData,
  UploadSupportMaterialRequest,
  UploadSupportMaterialResponse,
  UpdateSupportMaterialRequest,
  SupportMaterialStatistics,
  SyncResult,
  ReorderFileRequest,
  CreateFolderRequest,
  DeleteMultipleFilesRequest,
} from './types';

import { API_URL } from "../../utils/common";

// Helper para obtener token
const getAuthHeaders = () => {
  const token = localStorage.getItem('jwt');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

export class SupportMaterialService {
  
  // ==================== UPLOAD ====================
  
  /**
   * Solicita URL firmada para subir un archivo
   */
  static async getUploadUrl(request: UploadSupportMaterialRequest): Promise<UploadSupportMaterialResponse> {
    console.log('[Service] Solicitando URL de upload para:', request.fileName);
    
    const response = await fetch(`${API_URL}/support-material/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Error al obtener URL de subida' }));
      console.error('[Service] Error obteniendo URL:', error);
      throw new Error(error.message || 'Error al obtener URL de subida');
    }

    const result = await response.json();
    console.log('[Service] ✓ URL de upload recibida:', {
      fileName: result.fileName,
      folder: result.folder,
      urlLength: result.uploadUrl?.length,
      expiresIn: result.expiresIn
    });

    return result;
  }

  /**
   * Solicita URLs firmadas para múltiples archivos
   */
  static async getMultipleUploadUrls(
    files: UploadSupportMaterialRequest[]
  ): Promise<UploadSupportMaterialResponse[]> {
    console.log('[Service] Solicitando URLs para', files.length, 'archivos');
    console.log('[Service] POST', `${API_URL}/support-material/upload-multiple`);
    
    const startTime = Date.now();
    
    const response = await fetch(`${API_URL}/support-material/upload-multiple`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ files }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Service] Error HTTP:', response.status, errorText);
      throw new Error(`Error al obtener URLs de subida: ${response.status}`);
    }

    const results = await response.json();
    const duration = Date.now() - startTime;
    
    console.log('[Service] ✓ Recibidas', results.length, 'URLs de subida en', duration, 'ms');
    results.forEach((r: UploadSupportMaterialResponse, i: number) => {
      console.log(`  [${i + 1}/${results.length}]`, r.fileName, '→', r.folder, 
        `(URL length: ${r.uploadUrl?.length || 0})`);
    });

    return results;
  }

  /**
   * Sube un archivo a S3 usando la URL firmada
   * VERSIÓN CORREGIDA: Incluye metadata headers
   */
  static async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    metadata?: Record<string, string>
  ): Promise<void> {
    console.log('[Service] Subiendo archivo a S3:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      urlDomain: new URL(uploadUrl).hostname,
    });

    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentCompleted = Math.round((e.loaded * 100) / e.total);
          onProgress(percentCompleted);
          
          if (percentCompleted % 25 === 0) { // Log cada 25%
            console.log(`[Service] Upload progress: ${percentCompleted}% (${e.loaded}/${e.total} bytes)`);
          }
        }
      });

      // Success
      xhr.addEventListener('load', () => {
        const duration = ((Date.now() - startTime) / 1000).toFixed(2);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log(`[Service] ✓ Archivo subido exitosamente a S3 en ${duration}s:`, {
            fileName: file.name,
            status: xhr.status,
            duration: `${duration}s`,
          });
          resolve();
        } else {
          console.error(`[Service] ✗ Upload falló con status ${xhr.status}:`, {
            fileName: file.name,
            status: xhr.status,
            statusText: xhr.statusText,
            response: xhr.responseText,
          });
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      // Error
      xhr.addEventListener('error', () => {
        console.error('[Service] ✗ Error de red al subir archivo:', file.name);
        reject(new Error('Error de red al subir archivo'));
      });

      // Abort
      xhr.addEventListener('abort', () => {
        console.warn('[Service] ⚠ Upload cancelado:', file.name);
        reject(new Error('Upload cancelado'));
      });

      // Timeout
      xhr.timeout = 300000; // 5 minutos
      xhr.addEventListener('timeout', () => {
        console.error('[Service] ✗ Timeout al subir archivo:', file.name);
        reject(new Error('Timeout al subir archivo'));
      });

      // Preparar request
      xhr.open('PUT', uploadUrl);
      
      // Headers requeridos
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      
      // CRÍTICO: Agregar metadata headers si existen
      // S3 requiere que los headers de metadata coincidan con los de la presigned URL
      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
          const headerName = `x-amz-meta-${key.toLowerCase()}`;
          xhr.setRequestHeader(headerName, value);
          console.log(`[Service] Agregando header: ${headerName}: ${value}`);
        });
      }

      console.log('[Service] Iniciando PUT request a S3...');
      xhr.send(file);
    });
  }

  // ==================== READ ====================

  /**
   * Lista archivos por carpeta
   */
  static async listByFolder(folder: string): Promise<FolderData> {
    console.log('[Service] Listando archivos de carpeta:', folder);
    
    const response = await fetch(`${API_URL}/support-material/folder/${folder}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cargar archivos de la carpeta');
    }

    const result = await response.json();
    console.log('[Service] ✓ Cargados', result.files?.length || 0, 'archivos de', folder);
    return result;
  }

  /**
   * Lista todos los archivos agrupados por carpeta
   */
  static async listAllByFolders(): Promise<FolderData[]> {
    console.log('[Service] Listando todas las carpetas y archivos');
    
    const response = await fetch(`${API_URL}/support-material/folders`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cargar carpetas');
    }

    const result = await response.json();
    const totalFiles = result.reduce((sum: number, folder: FolderData) => sum + folder.totalFiles, 0);
    console.log('[Service] ✓ Cargadas', result.length, 'carpetas con', totalFiles, 'archivos en total');
    return result;
  }

  /**
   * Obtiene lista de nombres de carpetas
   */
  static async getFolders(): Promise<string[]> {
    const response = await fetch(`${API_URL}/support-material/folders/list`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cargar lista de carpetas');
    }

    const result = await response.json();
    console.log('[Service] ✓ Carpetas disponibles:', result);
    return result;
  }

  /**
   * Obtiene un archivo por ID
   */
  static async getFileById(id: number): Promise<SupportMaterialFile> {
    const response = await fetch(`${API_URL}/support-material/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cargar archivo');
    }

    return response.json();
  }

  // ==================== UPDATE ====================

  /**
   * Actualiza información de un archivo
   */
  static async updateFile(id: number, request: UpdateSupportMaterialRequest): Promise<SupportMaterialFile> {
    console.log('[Service] Actualizando archivo ID:', id, request);
    
    const response = await fetch(`${API_URL}/support-material/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Error al actualizar archivo');
    }

    const result = await response.json();
    console.log('[Service] ✓ Archivo actualizado:', result.fileName);
    return result;
  }

  /**
   * Activa o desactiva un archivo
   */
  static async toggleFileStatus(id: number, active: boolean, updatedBy?: string): Promise<void> {
    console.log('[Service] Cambiando status de archivo ID:', id, 'a', active);
    
    const params = new URLSearchParams({ 
      active: String(active),
      ...(updatedBy && { updatedBy })
    });

    const response = await fetch(`${API_URL}/support-material/${id}/toggle?${params}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cambiar estado del archivo');
    }

    console.log('[Service] ✓ Status actualizado');
  }

  /**
   * Reordena archivos en una carpeta
   */
  static async reorderFiles(folder: string, reorderRequests: ReorderFileRequest[]): Promise<void> {
    console.log('[Service] Reordenando', reorderRequests.length, 'archivos en', folder);
    
    const response = await fetch(`${API_URL}/support-material/folder/${folder}/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reorderRequests),
    });

    if (!response.ok) {
      throw new Error('Error al reordenar archivos');
    }

    console.log('[Service] ✓ Archivos reordenados');
  }

  // ==================== DELETE ====================

  /**
   * Elimina un archivo permanentemente
   */
  static async deleteFile(id: number, deletedBy?: string): Promise<void> {
    console.log('[Service] Eliminando archivo ID:', id);
    
    const params = deletedBy ? new URLSearchParams({ deletedBy }) : '';
    
    const response = await fetch(`${API_URL}/support-material/${id}${params ? `?${params}` : ''}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al eliminar archivo');
    }

    console.log('[Service] ✓ Archivo eliminado');
  }

  /**
   * Elimina múltiples archivos
   */
  static async deleteMultipleFiles(request: DeleteMultipleFilesRequest): Promise<void> {
    console.log('[Service] Eliminando', request.ids.length, 'archivos');
    
    const response = await fetch(`${API_URL}/support-material/batch`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Error al eliminar archivos');
    }

    console.log('[Service] ✓ Archivos eliminados');
  }

  /**
   * Elimina una carpeta completa
   */
  static async deleteFolder(folder: string, deletedBy?: string): Promise<void> {
    console.log('[Service] Eliminando carpeta:', folder);
    
    const params = deletedBy ? new URLSearchParams({ deletedBy }) : '';
    
    const response = await fetch(
      `${API_URL}/support-material/folder/${folder}${params ? `?${params}` : ''}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Error al eliminar carpeta');
    }

    console.log('[Service] ✓ Carpeta eliminada');
  }

  // ==================== UTILITIES ====================

  /**
   * Obtiene estadísticas
   */
  static async getStatistics(): Promise<SupportMaterialStatistics> {
    const response = await fetch(`${API_URL}/support-material/statistics`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al cargar estadísticas');
    }

    const result = await response.json();
    console.log('[Service] Estadísticas:', result);
    return result;
  }

  /**
   * Sincroniza BD con S3
   */
  static async syncWithS3(): Promise<SyncResult> {
    console.log('[Service] Sincronizando BD con S3...');
    
    const response = await fetch(`${API_URL}/support-material/sync`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al sincronizar con S3');
    }

    const result = await response.json();
    console.log('[Service] ✓ Sincronización completada:', result);
    return result;
  }

  /**
   * Crea una carpeta vacía
   */
  static async createFolder(request: CreateFolderRequest): Promise<void> {
    console.log('[Service] Creando carpeta:', request.folderName);
    
    const response = await fetch(`${API_URL}/support-material/folder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Error al crear carpeta');
    }

    console.log('[Service] ✓ Carpeta creada');
  }

  /**
   * Verifica si una carpeta existe
   */
  static async folderExists(folder: string): Promise<boolean> {
    const response = await fetch(`${API_URL}/support-material/folder/${folder}/exists`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Error al verificar carpeta');
    }

    const result = await response.json();
    console.log('[Service] Carpeta', folder, 'existe:', result);
    return result;
  }
}
